import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { JobContact } from '../types/job';

export function useJobContacts(jobId: string) {
  const [contacts, setContacts] = useState<JobContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContacts = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('nrm_job_contacts')
        .select(`
          *,
          contact:contact_id (
            id,
            first_name,
            last_name,
            work_email,
            work_phone,
            job_title
          )
        `)
        .eq('job_id', jobId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setContacts(data || []);
    } catch (err) {
      console.error('Failed to load job contacts:', err);
      setError('Unable to load contacts');
    } finally {
      setLoading(false);
    }
  };

  // Add contact
  const addContact = async (contactId: string, role: string, notes?: string) => {
    try {
      const { error: saveError } = await supabase
        .from('nrm_job_contacts')
        .insert([{
          job_id: jobId,
          contact_id: contactId,
          role,
          notes
        }]);

      if (saveError) throw saveError;
      await fetchContacts();
      return { error: null };
    } catch (err) {
      console.error('Failed to add job contact:', err);
      return { error: 'Failed to add contact' };
    }
  };

  // Remove contact
  const removeContact = async (contactId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('nrm_job_contacts')
        .delete()
        .eq('job_id', jobId)
        .eq('contact_id', contactId);

      if (deleteError) throw deleteError;
      await fetchContacts();
      return { error: null };
    } catch (err) {
      console.error('Failed to remove job contact:', err);
      return { error: 'Failed to remove contact' };
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [jobId]);

  return { 
    contacts, 
    loading, 
    error,
    addContact,
    removeContact,
    refetch: fetchContacts 
  };
}